import React from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { useSelector } from "react-redux";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import store from "../store/store";
import { servicesDataUpdate } from "../store/formJsonSlice";
import { useUpdateInitialJsonDataMutation, useReorderArrayMutation } from "../store/apiSlice";
import Loader from "../components/Loader";
import IconPicker from "../components/IconPicker";

// Separate component for Service Item with its own field array
const ServiceItem = ({
  serviceIndex,
  control,
  register,
  errors,
  removeService,
  provided,
}) => {
  const {
    fields: infoPointFields,
    append: appendInfoPoint,
    remove: removeInfoPoint,
  } = useFieldArray({
    control,
    name: `services.${serviceIndex}.infoPoints`,
  });

  return (
    <div
      ref={provided.innerRef}
      {...provided.draggableProps}
      className="border border-gray-200 p-4 rounded-lg bg-white"
    >
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center space-x-2">
          <div
            {...provided.dragHandleProps}
            className="cursor-move text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
              <path d="M7 2a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm6 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM7 8a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm6 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm-6 6a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm6 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4z" />
            </svg>
          </div>
          <h3 className="font-medium text-gray-700">
            Service {serviceIndex + 1}
          </h3>
        </div>
        <button
          type="button"
          onClick={() => removeService(serviceIndex)}
          className="text-red-600 hover:text-red-800 text-sm font-medium"
        >
          Remove
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Title *
          </label>
          <input
            {...register(`services.${serviceIndex}.title`, {
              required: "Service title is required",
            })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Enter service title"
          />
          {errors.services?.[serviceIndex]?.title && (
            <p className="text-red-500 text-sm mt-1">
              {errors.services[serviceIndex].title.message}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Price *
          </label>
          <input
            type="number"
            step="0.01"
            min="0"
            {...register(`services.${serviceIndex}.price`, {
              required: "Price is required",
              min: { value: 0, message: "Price must be positive" },
            })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Enter price"
          />
          {errors.services?.[serviceIndex]?.price && (
            <p className="text-red-500 text-sm mt-1">
              {errors.services[serviceIndex].price.message}
            </p>
          )}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Description *
        </label>
        <textarea
          {...register(`services.${serviceIndex}.description`, {
            required: "Description is required",
          })}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Enter service description"
        />
        {errors.services?.[serviceIndex]?.description && (
          <p className="text-red-500 text-sm mt-1">
            {errors.services[serviceIndex].description.message}
          </p>
        )}
      </div>

      <div className="mb-4 mt-1">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Icon *
        </label>
        <IconPicker
          control={control}
          name={`services.${serviceIndex}.icon`}
          required
        />

        {errors.services?.[serviceIndex]?.icon && (
          <p className="text-red-500 text-sm mt-1">
            {errors.services?.[serviceIndex].icon.message}
          </p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Info Points
        </label>
        <div className="space-y-2">
          {infoPointFields.map((field, pointIndex) => (
            <div key={field.id} className="flex items-center space-x-2">
              <input
                {...register(
                  `services.${serviceIndex}.infoPoints.${pointIndex}`
                )}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter info point"
              />
              <button
                type="button"
                onClick={() => removeInfoPoint(pointIndex)}
                className="text-red-600 hover:text-red-800 px-2 py-1"
                disabled={infoPointFields.length <= 1}
              >
                Remove
              </button>
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={() => appendInfoPoint("")}
          className="mt-2 text-blue-600 hover:text-blue-800 text-sm font-medium"
        >
          + Add Info Point
        </button>
      </div>
    </div>
  );
};

const Services = () => {
  const { services } = useSelector((state) => state.formJson);
  const [updateJson, { isLoading: isUpdateLoading }] = useUpdateInitialJsonDataMutation();
  const [reorderArray, { isLoading: isReorderLoading }] = useReorderArrayMutation();

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
  } = useForm({
    defaultValues: { ...services },
  });

  const {
    fields: serviceFields,
    append: appendService,
    remove: removeService,
    move: moveService,
  } = useFieldArray({
    control,
    name: "services",
  });

  const {
    fields: processFields,
    append: appendProcess,
    remove: removeProcess,
  } = useFieldArray({
    control,
    name: "process",
  });

  // Handle drag end for services
  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const { source, destination } = result;

    // Reorder locally using useFieldArray's move function
    moveService(source.index, destination.index);

    // Optional: Update the backend with new order
    try {
      await reorderArray({
        path: "services.services",
        fromIndex: source.index,
        toIndex: destination.index,
      }).unwrap();
    } catch (error) {
      console.error("Failed to reorder on server:", error);
      // Optionally revert the local change if server update fails
      moveService(destination.index, source.index);
    }
  };

  const onSubmit = async (data) => {
    store.dispatch(servicesDataUpdate({ ...data }));
    await updateJson(store.getState().formJson);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto bg-white rounded-lg shadow-md p-6">
        <h1 className="text-2xl font-bold text-gray-800 mb-6">
          Services and Process Form
        </h1>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
          {/* Main Title and Subtitle */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Main Title *
              </label>
              <input
                {...register("mainTitle", {
                  required: "Main title is required",
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter main title"
              />
              {errors.mainTitle && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.mainTitle.message}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sub Title *
              </label>
              <input
                {...register("subTitle", { required: "Sub title is required" })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter sub title"
              />
              {errors.subTitle && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.subTitle.message}
                </p>
              )}
            </div>
          </div>

          {/* Services Section with Drag and Drop */}
          <div className="border-t border-gray-200 pt-6">
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center space-x-3">
                <h2 className="text-xl font-semibold text-gray-700">Services</h2>
                {isReorderLoading && (
                  <span className="text-sm text-blue-600">Reordering...</span>
                )}
              </div>
              <button
                type="button"
                onClick={() =>
                  appendService({
                    title: "",
                    description: "",
                    infoPoints: [""],
                    price: "0.00",
                    icon: "",
                  })
                }
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
              >
                Add Service
              </button>
            </div>

            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="services">
                {(provided) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className="grid grid-cols-1 gap-6"
                  >
                    {serviceFields.map((service, serviceIndex) => (
                      <Draggable
                        key={service.id}
                        draggableId={service.id.toString()}
                        index={serviceIndex}
                      >
                        {(provided) => (
                          <ServiceItem
                            serviceIndex={serviceIndex}
                            control={control}
                            register={register}
                            errors={errors}
                            removeService={removeService}
                            provided={provided}
                          />
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </div>

          {/* Process Section */}
          <div className="border-t border-gray-200 pt-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-700">Process</h2>
              <button
                type="button"
                onClick={() => appendProcess({
                  title: "",
                  description: "",
                  icon: ""
                })}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
              >
                Add Process Step
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {processFields.map((process, processIndex) => (
                <div
                  key={process.id}
                  className="border border-gray-200 p-4 rounded-lg"
                >
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="font-medium text-gray-700">
                      Process Step {processIndex + 1}
                    </h3>
                    <button
                      type="button"
                      onClick={() => removeProcess(processIndex)}
                      className="text-red-600 hover:text-red-800 text-sm font-medium"
                    >
                      Remove
                    </button>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Title *
                      </label>
                      <input
                        {...register(`process.${processIndex}.title`, {
                          required: "Process title is required",
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter process title"
                      />
                      {errors.process?.[processIndex]?.title && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors.process[processIndex].title.message}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description *
                      </label>
                      <textarea
                        {...register(`process.${processIndex}.description`, {
                          required: "Description is required",
                        })}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter process description"
                      />
                      {errors.process?.[processIndex]?.description && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors.process[processIndex].description.message}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Icon *
                      </label>
                      <IconPicker
                        control={control}
                        name={`process.${processIndex}.icon`}
                        required
                      />

                      {errors.process?.[processIndex]?.icon && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors.process?.[processIndex].icon.message}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-center pt-6">
            <button
              type="submit"
              disabled={isUpdateLoading || isReorderLoading}
              className="bg-green-600 text-white px-8 py-3 rounded-md hover:bg-green-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {(isUpdateLoading || isReorderLoading) && <Loader />}
              <span>Submit Form</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Services;